/**
 * Authentication Service
 * 
 * @swagger-tag Auth
 * @description Authentication endpoints for login, logout, and token refresh.
 */
import type { components } from "@/core/types/generated/api";
import ApiService from "./api";

type UserDto = components["schemas"]["UserDto"]; // runtime current user shape
type UserAuth = components["schemas"]["UserAuth"]; // auth tokens shape

/**
 * AuthService – aligned with OpenAPI spec endpoints.
 * Legacy custom endpoints `/auth/me` & `/auth/change-password` replaced by `/Users/me` & `/Users/me/change-password`.
 */
export const AuthService = {
    async login(email: string, password: string): Promise<UserAuth | null> {
        const { data } = await ApiService.post<UserAuth>('/auth/login', { email, password });
        return data || null;
    },

    async refreshToken(refreshToken: string): Promise<UserAuth | null> {
        const { data } = await ApiService.post<UserAuth>('/auth/refresh', { refreshToken });
        return data || null;
    },

    async logout(): Promise<boolean> {
        try {
            await ApiService.post('/auth/logout');
            return true;
        } catch (error) {
            console.warn('Logout API call failed:', error);
            return false;
        }
    },

    /**
     * Retrieves current user (optionally with project memberships when backend supports query flag).
     */
    async getCurrentUser(includeProjectMemberships = false): Promise<UserDto | null> {
        const { data } = await ApiService.get<UserDto>('/Users/me', {
            params: includeProjectMemberships ? { includeProjectMemberships: true } : undefined,
        });
        return data || null;
    },

    /**
     * Changes the current user's password.
     * Returns true on 204 No Content.
     */
    async changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
        const response = await ApiService.post('/Users/me/change-password', { currentPassword, newPassword });
        return response.status === 204;
    },

    /**
     * Updates the current user's profile information.
     * Note: This uses the generic user update endpoint with the current user's ID.
     */
    async updateCurrentUserProfile(profileData: { fullName?: string; email?: string; designation?: string }): Promise<UserDto | null> {
        // First get current user to get the ID
        const currentUser = await this.getCurrentUser();
        if (!currentUser?.id) {
            throw new Error('Cannot update profile: current user ID not available');
        }

        const { data } = await ApiService.put<UserDto>(`/Users/${currentUser.id}`, profileData);
        return data || null;
    }
};
